import React, { useState, useEffect, useCallback } from 'react';
import BloodVolumeCalculator from '../BloodVolumeCalculator';
import './GuidedFoodWaterBlood.css';

interface FoodWaterRestriction {
  detected: boolean;
  primaryAnswer: string;
  duration: string;
  justification: string;
  confidence: string;
  evidence: string[];
}

interface BloodProcedure {
  site: string;
  volume_ml: number;
  frequency: string;
  collections_per_week: number;
  weight_g: number;
  is_terminal: boolean;
  description: string;
}

interface BloodCollection {
  detected: boolean;
  primaryAnswer: string;
  procedures: BloodProcedure[];
  confidence: string;
  evidence: string[];
}

interface StructuredData {
  foodWaterRestriction: FoodWaterRestriction;
  bloodCollection: BloodCollection;
  shouldDisplay: boolean;
  reasoning: string;
}

interface GuidedFoodWaterBloodProps {
  protocolId: number;
  apiBaseUrl: string;
  getAuthHeaders: () => Record<string, string>;
  onAccepted: (data: any) => void;
  onError: (error: string) => void;
  onSkip: () => void;
}

const GuidedFoodWaterBlood: React.FC<GuidedFoodWaterBloodProps> = ({
  protocolId,
  apiBaseUrl,
  getAuthHeaders,
  onAccepted,
  onError,
  onSkip
}) => {
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Structured data from backend
  const [structured, setStructured] = useState<StructuredData | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  // Q5.3 editable fields
  const [q53Answer, setQ53Answer] = useState<'Yes' | 'No'>('No');
  const [q53Duration, setQ53Duration] = useState('');
  const [q53Justification, setQ53Justification] = useState('');
  const [q53Ack1, setQ53Ack1] = useState(false); // weight_monitoring
  const [q53Ack2, setQ53Ack2] = useState(false); // daily_records

  // Q5.4 editable fields
  const [q54Answer, setQ54Answer] = useState<'Yes' | 'No'>('No');
  const [q54Procedures, setQ54Procedures] = useState<BloodProcedure[]>([]);
  const [q54Acknowledgement, setQ54Acknowledgement] = useState<'volumes_compliant' | 'volumes_exception' | ''>('');

  // Generate analysis on mount
  const generateAnalysis = useCallback(async () => {
    try {
      setIsGenerating(true);
      setParseError(null);

      const response = await fetch(`${apiBaseUrl}/api/guided-conversation/${protocolId}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          phaseNumber: 5
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate analysis');
      }

      const data = await response.json();

      // Check if phase should display
      if (!data.shouldDisplay) {
        // Phase not applicable - parent will show skip option
        setStructured(null);
        setIsLoading(false);
        setIsGenerating(false);
        return;
      }

      // Check if we got structured data
      if (data.isStructured && data.structured) {
        setStructured(data.structured);

        // Pre-fill Q5.3 from AI detection
        const fw = data.structured.foodWaterRestriction;
        if (fw.detected) {
          setQ53Answer('Yes');
          setQ53Duration(fw.duration || '');
          setQ53Justification(fw.justification || '');
        } else {
          setQ53Answer('No');
        }

        // Pre-fill Q5.4 from AI detection
        const blood = data.structured.bloodCollection;
        if (blood.detected && blood.procedures.length > 0) {
          setQ54Answer('Yes');
          setQ54Procedures(blood.procedures);
        } else {
          setQ54Answer('No');
          setQ54Procedures([]);
        }

      } else {
        // Fallback - no structured data
        setParseError(data.parseError || 'Received unstructured response');
      }

    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to generate analysis');
    } finally {
      setIsGenerating(false);
      setIsLoading(false);
    }
  }, [apiBaseUrl, protocolId, getAuthHeaders, onError]);

  useEffect(() => {
    generateAnalysis();
  }, [generateAnalysis]);

  // Handle blood procedure changes
  const updateProcedure = (index: number, field: keyof BloodProcedure, value: any) => {
    setQ54Procedures(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addProcedure = () => {
    setQ54Procedures(prev => [...prev, {
      site: '',
      volume_ml: 0.3,
      frequency: '',
      collections_per_week: 1,
      weight_g: 30,
      is_terminal: false,
      description: ''
    }]);
  };

  const removeProcedure = (index: number) => {
    setQ54Procedures(prev => prev.filter((_, i) => i !== index));
  };

  // Validate before accept
  const validate = (): boolean => {
    if (q53Answer === 'Yes') {
      if (!q53Duration.trim()) {
        onError('Q5.3: Please specify duration of food/water restriction');
        return false;
      }
      if (!q53Justification.trim()) {
        onError('Q5.3: Please provide scientific justification');
        return false;
      }
      if (!q53Ack1 || !q53Ack2) {
        onError('Q5.3: Please acknowledge both requirements');
        return false;
      }
    }

    if (q54Answer === 'Yes') {
      if (q54Procedures.length === 0) {
        onError('Q5.4: Please add at least one blood collection procedure');
        return false;
      }
      for (let i = 0; i < q54Procedures.length; i++) {
        const proc = q54Procedures[i];
        if (!proc.site.trim()) {
          onError(`Q5.4 Procedure ${i + 1}: Please specify collection site`);
          return false;
        }
        if (!proc.frequency.trim()) {
          onError(`Q5.4 Procedure ${i + 1}: Please specify frequency`);
          return false;
        }
        if (!proc.description.trim()) {
          onError(`Q5.4 Procedure ${i + 1}: Please provide description`);
          return false;
        }
      }
      if (!q54Acknowledgement) {
        onError('Q5.4: Please select one acknowledgement option');
        return false;
      }
    }

    return true;
  };

  // Handle accept
  const handleAccept = () => {
    if (!validate()) return;

    setIsSaving(true);

    // Format data for backend
    const data = {
      q5_3: {
        primaryAnswer: q53Answer,
        duration: q53Duration,
        justification: q53Justification,
        acknowledgements: q53Answer === 'Yes'
          ? ['weight_monitoring', 'daily_records']
          : []
      },
      q5_4: {
        primaryAnswer: q54Answer,
        procedures: q54Procedures,
        acknowledgements: q54Answer === 'Yes'
          ? [q54Acknowledgement]
          : []
      }
    };

    onAccepted(data);
  };

  // Handle regenerate
  const handleRegenerate = () => {
    setIsLoading(true);
    generateAnalysis();
  };

  if (isLoading || isGenerating) {
    return (
      <div className="guided-phase-loading">
        <div className="spinner"></div>
        <p>Analyzing experimental design for food/water restriction and blood collection...</p>
      </div>
    );
  }

  if (parseError) {
    return (
      <div className="guided-phase-error">
        <h3>Analysis Error</h3>
        <p>{parseError}</p>
        <button onClick={handleRegenerate} className="regenerate-button">
          Try Again
        </button>
        <button onClick={onSkip} className="skip-button">
          Skip This Phase
        </button>
      </div>
    );
  }

  if (!structured) {
    return (
      <div className="guided-phase-not-applicable">
        <p>No food/water restriction or blood collection detected.</p>
        <button onClick={onSkip} className="skip-button">
          Skip to Next Phase
        </button>
      </div>
    );
  }

  return (
    <div className="guided-food-water-blood">
      <h3>Phase 5: Food/Water Restriction & Blood Collection</h3>

      {/* Detection Summary */}
      <div className="detection-summary">
        <h4>AI Detection Summary</h4>
        <p><strong>Reasoning:</strong> {structured.reasoning}</p>

        {structured.foodWaterRestriction.detected && (
          <div className="detection-item">
            <h5>Food/Water Restriction Detected</h5>
            <p><strong>Confidence:</strong> {structured.foodWaterRestriction.confidence}</p>
            <p><strong>Evidence:</strong></p>
            <ul>
              {structured.foodWaterRestriction.evidence.map((ev, idx) => (
                <li key={idx}>{ev}</li>
              ))}
            </ul>
          </div>
        )}

        {structured.bloodCollection.detected && (
          <div className="detection-item">
            <h5>Blood Collection Detected</h5>
            <p><strong>Confidence:</strong> {structured.bloodCollection.confidence}</p>
            <p><strong>Evidence:</strong></p>
            <ul>
              {structured.bloodCollection.evidence.map((ev, idx) => (
                <li key={idx}>{ev}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Q5.3 Section */}
      <div className="question-section">
        <h4>Q5.3: Food or Water Restriction</h4>
        <p>Will any animals undergo food or water restriction?</p>

        <div className="radio-group">
          <label>
            <input
              type="radio"
              checked={q53Answer === 'Yes'}
              onChange={() => setQ53Answer('Yes')}
            />
            Yes
          </label>
          <label>
            <input
              type="radio"
              checked={q53Answer === 'No'}
              onChange={() => setQ53Answer('No')}
            />
            No
          </label>
        </div>

        {q53Answer === 'Yes' && (
          <div className="followup-section">
            <div className="form-group">
              <label>How long will animals be food/water restricted?</label>
              <input
                type="text"
                value={q53Duration}
                onChange={(e) => setQ53Duration(e.target.value)}
                placeholder="e.g., 16 hours overnight"
              />
            </div>

            <div className="form-group">
              <label>Why is this restriction scientifically necessary?</label>
              <textarea
                value={q53Justification}
                onChange={(e) => setQ53Justification(e.target.value)}
                rows={4}
                placeholder="Provide scientific justification for the restriction"
              />
            </div>

            <div className="acknowledgements">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={q53Ack1}
                  onChange={(e) => setQ53Ack1(e.target.checked)}
                />
                I acknowledge that animal body weights will be monitored regularly during food/water restriction.
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={q53Ack2}
                  onChange={(e) => setQ53Ack2(e.target.checked)}
                />
                I acknowledge that daily records will be maintained for all restricted animals.
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Q5.4 Section */}
      <div className="question-section">
        <h4>Q5.4: Blood Collection</h4>
        <p>Will blood be collected from any animals?</p>

        <div className="radio-group">
          <label>
            <input
              type="radio"
              checked={q54Answer === 'Yes'}
              onChange={() => setQ54Answer('Yes')}
            />
            Yes
          </label>
          <label>
            <input
              type="radio"
              checked={q54Answer === 'No'}
              onChange={() => setQ54Answer('No')}
            />
            No
          </label>
        </div>

        {q54Answer === 'Yes' && (
          <div className="followup-section">
            <h5>Blood Collection Procedures</h5>

            {q54Procedures.map((proc, idx) => (
              <div key={idx} className="procedure-item">
                <div className="procedure-header">
                  <h6>Procedure {idx + 1}</h6>
                  <button
                    onClick={() => removeProcedure(idx)}
                    className="remove-button"
                    type="button"
                  >
                    Remove
                  </button>
                </div>

                <div className="form-group">
                  <label>Collection Site:</label>
                  <input
                    type="text"
                    value={proc.site}
                    onChange={(e) => updateProcedure(idx, 'site', e.target.value)}
                    placeholder="e.g., retro-orbital, tail vein"
                  />
                </div>

                <div className="form-group">
                  <label>Frequency:</label>
                  <input
                    type="text"
                    value={proc.frequency}
                    onChange={(e) => updateProcedure(idx, 'frequency', e.target.value)}
                    placeholder="e.g., weekly for 8 weeks"
                  />
                </div>

                <div className="form-group">
                  <label>Terminal Collection?</label>
                  <input
                    type="checkbox"
                    checked={proc.is_terminal}
                    onChange={(e) => updateProcedure(idx, 'is_terminal', e.target.checked)}
                  />
                </div>

                <div className="form-group">
                  <label>Description (include anesthesia method):</label>
                  <textarea
                    value={proc.description}
                    onChange={(e) => updateProcedure(idx, 'description', e.target.value)}
                    rows={3}
                    placeholder="e.g., Blood (0.3 mL) collected from retro-orbital sinus using heparinized capillary tubes under brief isoflurane anesthesia."
                  />
                </div>

                {/* Embedded Blood Volume Calculator */}
                <div className="calculator-embed">
                  <BloodVolumeCalculator
                    weight={proc.weight_g}
                    volumePerCollection={proc.volume_ml}
                    collectionsPerWeek={proc.collections_per_week}
                    onWeightChange={(val) => updateProcedure(idx, 'weight_g', val)}
                    onVolumeChange={(val) => updateProcedure(idx, 'volume_ml', val)}
                    onFrequencyChange={(val) => updateProcedure(idx, 'collections_per_week', val)}
                    compact={true}
                  />
                </div>
              </div>
            ))}

            <button onClick={addProcedure} className="add-button" type="button">
              Add Another Blood Collection Procedure
            </button>

            <div className="acknowledgements">
              <p><strong>Blood Volume Compliance:</strong></p>
              <label className="radio-label">
                <input
                  type="radio"
                  checked={q54Acknowledgement === 'volumes_compliant'}
                  onChange={() => setQ54Acknowledgement('volumes_compliant')}
                />
                All blood collection volumes are within institutional limits (≤1% per collection, ≤1% weekly, ≤7.5% monthly).
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  checked={q54Acknowledgement === 'volumes_exception'}
                  onChange={() => setQ54Acknowledgement('volumes_exception')}
                />
                Some volumes exceed limits. Scientific justification and IACUC approval will be obtained separately.
              </label>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="phase-actions">
        <button
          onClick={handleAccept}
          disabled={isSaving}
          className="accept-button"
        >
          {isSaving ? 'Saving...' : 'Accept & Continue'}
        </button>
        <button
          onClick={handleRegenerate}
          disabled={isGenerating || isSaving}
          className="regenerate-button"
        >
          Regenerate Analysis
        </button>
        <button
          onClick={onSkip}
          disabled={isSaving}
          className="skip-button"
        >
          Skip This Phase
        </button>
      </div>
    </div>
  );
};

export default GuidedFoodWaterBlood;
